import { ArrowDown, X } from 'react-feather';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RegistrationSummary } from '../helpers';
import { Section, SectionTitle } from '../../Common/Layout';
import { useRegistrationsUrlState } from './helpers';
import RegistrationDetails from './RegistrationDetails';
import UI from '../../../common/UI';
import useMemoizedValue from '../../../common/useMemoizedValue';
import useResaleState from '../../../common/useResaleState';

export interface WaitlistRegistrationsSectionProps {
  summary: RegistrationSummary;
  perPage?: number;
}

const WaitlistRegistrationsSection = ({ summary, perPage = 10 }: WaitlistRegistrationsSectionProps) => {
  const { t } = useTranslation();

  const [urlState, setUrlState] = useRegistrationsUrlState();

  const showMore = () => {
    setUrlState((params) => ({
      ...params,
      waitlist_registrations: {
        ...params.waitlist_registrations,
        limit: params.waitlist_registrations.limit + perPage,
      },
    }), true);
  };

  const showLess = () => {
    setUrlState((params) => ({
      ...params,
      waitlist_registrations: {
        ...params.waitlist_registrations,
        limit: null,
      },
    }), true);
  };

  const [loading, setLoading] = useState(false);
  const params = useMemoizedValue(urlState.waitlist_registrations);

  const { publicResale } = useResaleState(summary.event);

  useEffect(() => {
    setLoading(true);
  }, [params]);

  useEffect(() => {
    setLoading(false);
  }, [summary]);

  const registrations = summary.waitlist_registrations.slice(0, urlState.waitlist_registrations.limit);

  return (
    <UI.GridContainer sc={{ gutter: 0.75 }} id="TicketsSection">
      <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <SectionTitle
          count={summary.waitlist_registrations_count > 1 ? summary.waitlist_registrations_count : undefined}
          loading={loading}
        >
          {t('waitlist')}
        </SectionTitle>
        <UI.Small>
          <UI.StatusIndicator
            sc={{ background: publicResale.enabled ? 'success' : 'error', mr: 1.5 }}
          />
          <UI.A href={summary.event.resale_url} sc={{ color: 'gray.500' }} target="_blank" rel="noopener noreferrer">
            {publicResale.message}
          </UI.A>
        </UI.Small>
      </UI.FlexContainer>

      <UI.GridContainer sc={{ gutter: 1 / 3, loading }}>
        {registrations.map((registration) => (
          <RegistrationDetails
            registration={registration}
            summary={summary}
            key={registration.id}
          />
        ))}

        {registrations.length === 0 && (
          <Section secondary>
            <UI.FlexContainer sc={{ alignItems: 'center' }} style={{ minHeight: 80 }}>
              <UI.Div sc={{ textAlign: 'center', width: 100, color: 'gray.600' }}>
                {t('no_results')}
              </UI.Div>
            </UI.FlexContainer>
          </Section>
        )}

        {summary.waitlist_registrations_count > perPage && (
          <UI.GridContainer
            sc={{
              columns: urlState.waitlist_registrations.limit > perPage ? 'fit-content(150px) 2fr' : '1fr',
              gutter: 0.5,
              px: [2, 4],
            }}
          >
            {urlState.waitlist_registrations.limit > perPage && (
              <UI.Button
                onClick={() => showLess()}
                sc={{ outline: true, borderRadius: 'xl', size: 'lg', fontSize: 1 }}
              >
                <UI.Icon>
                  <X />
                </UI.Icon>
                {' '}
                {t('hide')}
              </UI.Button>
            )}
            <UI.Button
              onClick={() => showMore()}
              disabled={urlState.waitlist_registrations.limit >= summary.waitlist_registrations_count || loading}
              sc={{ outline: true, borderRadius: 'xl', size: 'lg', fontSize: 1 }}
            >
              <UI.Icon>
                <ArrowDown />
              </UI.Icon>
              {' '}
              {t('show_more')}
            </UI.Button>
          </UI.GridContainer>
        )}
      </UI.GridContainer>
    </UI.GridContainer>
  );
};

export default WaitlistRegistrationsSection;
